import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  RgbaColor,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import { selectActors, selectAllPropPresets } from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import usePropHelper from "../../hooks/PropHelper";
import Take3D from "../../models/Take3D";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../sequence/EnvironmentPositionContext";
import { AttachmentMember } from "./SequenceAttachmentMarker";

const PLAYER_ID = "player";

const toCss = (color?: RgbaColor) =>
  color
    ? `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, ${color.a})`
    : undefined;

/**
 * What the attachment markers on the map need for a take: where positions are
 * on the map, the take location's colour, and how actors and props look and
 * are named.
 */
export default function useAttachmentMarkerParts(take: Take3D | undefined) {
  const actors = useSelector(selectActors);
  const propPresets = useSelector(selectAllPropPresets);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const { getSituationMetadata } = useSituationHelper();
  const { tryGetUsedPropByInstanceId } = usePropHelper();

  const points = useMemo(() => {
    // Where each position is on the map, and which way it faces (camera arrows).
    const byId = new Map<
      string,
      { x: number; y: number; direction?: { x: number; y: number } }
    >();
    getEnvironmentPositionContexts(
      takePositions ?? [],
      take?.situations,
      getSituationMetadata,
    ).forEach((context) =>
      [...context.actorPositions, ...context.propPositions, ...context.cameraPositions].forEach(
        (position) => {
          const point = { ...position.position, direction: position.direction };
          byId.set(getSituationPositionId(context, position.id), point);
          if (!byId.has(position.id)) byId.set(position.id, point);
        },
      ),
    );
    return byId;
  }, [take?.situations, getSituationMetadata, takePositions]);

  // Markers draw their border (and the Player) in the take location's colour.
  const takeColorRgba = (takePositions ?? []).find(
    (point) => point.id === take?.interactionPointId,
  )?.take_color;
  const takeColor = toCss(takeColorRgba);

  const actorName = (id: string) =>
    id === PLAYER_ID ? "Player" : actors?.find((actor) => actor.id === id)?.workingName ?? "An actor";
  const propName = (id: string) => propPresets?.find((prop) => prop.id === id)?.name ?? "A prop";
  const actorMember = (id: string): AttachmentMember => ({
    kind: "actor",
    actorId: id,
    actor: actors?.find((actor) => actor.id === id),
  });
  const propMember = (id: string): AttachmentMember => ({
    kind: "prop",
    imageUrl: tryGetUsedPropByInstanceId(id)?.propContent.downloadUri,
  });
  const placementOf = (propId: string) =>
    take?.props?.find((prop) => prop.instanceId === propId)?.positionId;

  return {
    points,
    takeColor,
    takeColorRgba,
    actorName,
    propName,
    actorMember,
    propMember,
    placementOf,
  };
}
